import { Badge } from '@abyss/web/ui/Badge';
import { Flex } from '@abyss/web/ui/Flex';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { useTranslation } from 'react-i18next';

import { usePrimaryCare } from '../../hooks/usePrimaryCare';
import { getShowEditPCP } from '../../utils/primaryCare.utils';
import { getLoggedInMember } from '../../utils/user.utils';
import { formatDateWithoutLeadingZeros } from '../Utils/datesUtils/formatDate';
import { CareTeamDropDown } from './CareTeamProviderCard.styled';

export const ProviderCardHeader = ({
  isPcP,
  menuItems,
  isExternalPlanMember,
  isFuturePcpCard = false,
  pcpEffectiveStartDate,
  pcpEffectiveEndDate,
  showEffectiveStartDate = false,
  showEffectiveEndDate = false,
  showChooseOrEditPcpCTA,
}: {
  isPcP: boolean;
  menuItems: {
    title: string;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  }[];
  isExternalPlanMember: boolean;
  isFuturePcpCard?: boolean;
  pcpEffectiveStartDate?: string;
  pcpEffectiveEndDate?: string;
  showEffectiveStartDate?: boolean;
  showEffectiveEndDate?: boolean;
  showChooseOrEditPcpCTA?: boolean;
}) => {
  const { t } = useTranslation();
  const { data } = usePrimaryCare();
  const { primaryCare } = data;
  const loggedInMemberInfo = getLoggedInMember();
  const lockInIndicator = primaryCare?.[0]?.lockInIndicator;
  const showEditPCP = getShowEditPCP(
    loggedInMemberInfo.editPcp,
    lockInIndicator
  );

  const hasCurrentPcp = primaryCare?.[0]?.pcpInfo?.hasPCP;
  const shouldHideEditButton = isFuturePcpCard && hasCurrentPcp;

  return (
    <Flex
      alignItems={isFuturePcpCard ? 'flex-start' : 'center'}
      direction={isFuturePcpCard ? 'column' : 'row'}
      justify={isFuturePcpCard ? 'flex-start' : 'space-between'}
      style={{
        width: '100%',
        margin: isFuturePcpCard ? '8px' : undefined,
      }}
    >
      {isPcP && !isFuturePcpCard && (
        <Badge data-testid="pcp-badge" outline variant="success">
          {t('PCP')}
          {showEffectiveEndDate &&
            pcpEffectiveEndDate &&
            ` ${t('• until')} ${formatDateWithoutLeadingZeros(
              pcpEffectiveEndDate,
              true
            )}`}
        </Badge>
      )}
      {isFuturePcpCard && (
        <Badge
          css={{ minWidth: 'fit-content' }}
          data-testid="future-pcp-badge"
          outline
          variant="info"
        >
          {t('Future PCP')}
          {showEffectiveStartDate &&
            pcpEffectiveStartDate &&
            ` ${t('• starting')} ${formatDateWithoutLeadingZeros(
              pcpEffectiveStartDate,
              true
            )}`}
        </Badge>
      )}
      {!(isExternalPlanMember && isPcP) &&
        !shouldHideEditButton &&
        showChooseOrEditPcpCTA !== false && (
          <Flex
            data-testid="care-team-dropdown-wrapper"
            justify={isFuturePcpCard ? 'flex-start' : 'flex-end'}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
            }}
            style={{
              flex: isFuturePcpCard ? 'none' : 1,
            }}
          >
            {(showEditPCP || !isPcP || isFuturePcpCard) && (
              <CareTeamDropDown
                after={<IconSymbol icon="keyboard_arrow_down" />}
                css={
                  isFuturePcpCard
                    ? { padding: '0px', marginLeft: '4px' }
                    : undefined
                }
                data-testid="care-team-dropdown"
                label={
                  isPcP || isFuturePcpCard
                    ? t('PRIMARY_CARE_PROVIDER.EDIT_PCP')
                    : t('PRIMARY_CARE_PROVIDER.EDIT')
                }
                menuItems={menuItems}
                outline={false}
              />
            )}
          </Flex>
        )}
    </Flex>
  );
};
